import { Box, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import _ from 'lodash';
import { FunctionComponent, useEffect, useState } from 'react';
import { QuestionSetOperations } from '../data/Firestore';
import { gameConfigStore, useGameConfigSelector } from '../store/GameConfigStore';
import { useAppDispatch } from '../store/Hooks';
import { QuestionSetConfig, QuestionSetId } from '../types/GameplayTypes';
import { isQuestionSetPlayable } from '../util/GameConfigUtils';
import { pluralize } from '../util/Text';
import { LoadComponent, useLoadingState } from './admin/Loading';

export const QuestionSetSelect: FunctionComponent = () => {
	const [allSets, setAllSets] = useState<QuestionSetConfig[]>([]);

	const selected = useGameConfigSelector(gameConfigStore.selectors.questionSet);
	const value = allSets.empty() ? '' : (selected?.id ?? '');

	const dispatch = useAppDispatch();

	function itemName(questionSet: QuestionSetConfig) {
		return questionSet.isActive ? questionSet.name : `${questionSet.name} (inactive)`;
	}

	const { load, loading, error } = useLoadingState({
		message: 'Loading Question Sets...',
		failureMessage: 'Unable to load Question Sets. Please reload the page to try again.'
	}, async () => {
		const sets = await QuestionSetOperations.getAll();
		const sortedByActive = _.sortBy(sets, s => !s.isActive);
		setAllSets(sortedByActive);

		// If no existing selection exists, default to first playable set
		const defaultSet = sortedByActive.find(isQuestionSetPlayable);
		if ((selected === undefined) && (defaultSet !== undefined)) {
			dispatch(gameConfigStore.actions.setQuestionSet(defaultSet));
		}
	});

	useEffect(function loadOnMount() {
		void load();
	}, []);

	function onChange(e: SelectChangeEvent<QuestionSetId>) {
		const set = allSets.find(qs => qs.id === e.target.value);
		dispatch(gameConfigStore.actions.setQuestionSet(set));
	}

	return <LoadComponent loading={loading} error={error}>
		<FormControl fullWidth>
			<InputLabel>Question Set</InputLabel>
			<Select value={value} onChange={onChange} label='Question Set'>
				{allSets.map(set =>
					<MenuItem key={set.id} value={set.id} disabled={!isQuestionSetPlayable(set)}>
						<Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 2 }}>
							<span>{itemName(set)}</span>
							<Box component='span' sx={{ color: 'text.secondary' }}>
								{pluralize(set.questionIds.length, 'question', { quantityPrefix: true })}
							</Box>
						</Box>
					</MenuItem>)}
			</Select>
		</FormControl>
	</LoadComponent>;
};